import {Icon} from '@atoms';
import {useModal} from '@hooks';
import {trackEvent} from '@lib/infrastructure';
import {useAppSelector, getIsMapLoaded, getFloorById, ConnectWorkspaceReservation} from '@lib/store';
import {WorkspaceReservation} from '@lib/store/src/data/timeslots/apiTypes';
import {FacilityMarkerData} from '@map/mapiq-map/markers/NodeMarkerData';
import {MapView, ExpandMapButton} from '@molecules';
import {cn} from '@utils';
import {useGlobalMapTypePreference} from 'src/components/molecules/MapView/useGlobalMapTypePreference';
import {BigMapModal} from 'src/components/organisms/BigMapModal';
import {useFacilityMarkers} from 'src/components/organisms/hereAndNow/useFacilityMarkers';

const abortIfAlreadyInView = false;
const deskViewportRestriction = {
  paddingBottomPixels: 100,
  paddingLeftPixels: 60,
  paddingRightPixels: 60,
  paddingTopPixels: 100,
} as const;

const otherViewportRestriction = {
  paddingBottomPixels: 60,
  paddingLeftPixels: 60,
  paddingRightPixels: 60,
  paddingTopPixels: 60,
} as const;

export const TileMapPreview = ({
  workspaceReservation,
}: {
  workspaceReservation: WorkspaceReservation | ConnectWorkspaceReservation;
}) => {
  const {openModal, setModalPages, setBigPages} = useModal();

  const location = {
    buildingName: workspaceReservation.workspace.buildingName,
    buildingId: workspaceReservation.workspace.buildingId,
    floorName: 'floorName' in workspaceReservation.workspace ? workspaceReservation.workspace.floorName : null,
    floorId: 'floorId' in workspaceReservation.workspace ? workspaceReservation.workspace.floorId : null,
    areaName: 'areaName' in workspaceReservation.workspace ? workspaceReservation.workspace.areaName : null,
    areaId: 'areaId' in workspaceReservation.workspace ? workspaceReservation.workspace.areaId : null,
    nodeName: workspaceReservation.workspace.nodeName,
    nodeId: workspaceReservation.workspace.nodeId,
  };

  const facilityMarkers = useFacilityMarkers(location.buildingId, location.floorId);

  const [type] = useGlobalMapTypePreference('MapPreview');
  const mapIsLoaded = useAppSelector(getIsMapLoaded(location.buildingId, location.floorId, type));

  const expandMap = () => {
    if (!workspaceReservation) return;
    const workspaceLabel = workspaceReservation
      ? [location.floorName, location.areaName, location.nodeName === location.areaName ? null : location.nodeName]
          .filter((x) => x)
          .join(', ')
      : 'No workspace reservation';

    const mapView = (
      <BigMapModal
        title="Workspace"
        main={
          <MapView
            buildingId={location.buildingId!}
            floorId={location.floorId!}
            highlights={[...facilityMarkers, mapMarker]}
            buildingNodeStates={new Map([[mapMarker.nodeId, 'selected']])}
            buildingNodeTypeStates={new Map([['facility', 'dimmed']])}
            fullView={true}
            borderRadius={0}
            disablePointerEvents={false}
            elementsToKeepInView={[mapMarker.nodeId]}
            abortIfAlreadyInView={abortIfAlreadyInView}
            viewportRestrictions={
              workspaceReservation.workspace.nodeType === 'Desk' ? deskViewportRestriction : otherViewportRestriction
            }
          />
        }
        footer={
          <div className="flex gap-4 justify-start p-4">
            <div className="flex flex-col justify-center items-center bg-beige-500* h-12 w-12 rounded-lg flex-shrink-0">
              <Icon
                icon="desk"
                size="24px"
              />
            </div>
            <div className="flex flex-col">
              <h4>{location.areaName ?? location.floorName ?? location.buildingName}</h4>
              <p>{workspaceLabel}</p>
            </div>
          </div>
        }
      />
    );

    trackEvent('Home_WorkdayTile__ExpandMap');
    setModalPages([mapView]);
    setBigPages([mapView]);
    openModal();
  };

  const mapAvailable = useAppSelector((state) => {
    if (!location.floorId) return false;

    const floor = getFloorById(state, location.floorId);
    if (!floor) return false;

    return floor.hasMap;
  });

  if (!location.buildingId || !location.floorId || !mapAvailable || !location.areaId) return null;

  const mapMarker: FacilityMarkerData = {
    type: 'facility',
    subType: 'Area',
    nodeId: location.nodeId,
  };

  return (
    <div className={cn('aspect-video col-span-full max-h-60 w-full relative overflow-hidden h-full')}>
      <MapView
        buildingId={location.buildingId}
        floorId={location.floorId}
        highlights={[...facilityMarkers, mapMarker]}
        buildingNodeStates={new Map([[mapMarker.nodeId, 'selected']])}
        buildingNodeTypeStates={new Map([['facility', 'dimmed']])}
        fullView={true}
        borderRadius={0}
        disablePointerEvents={true}
        abortIfAlreadyInView={abortIfAlreadyInView}
        elementsToKeepInView={[mapMarker.nodeId]}
        viewportRestrictions={
          workspaceReservation.workspace.nodeType === 'Desk' ? deskViewportRestriction : otherViewportRestriction
        }
      />
      <ExpandMapButton
        onClick={expandMap}
        className={'absolute top-4 right-4'}
      />
    </div>
  );
};
